"use client";
import Section from "@/components/Section";
import { Check } from "lucide-react";
import ProductPage from "@/components/ProductPage";
import ImageHero from "./eschborn.jpg";
import ImageHighlight from "./eschborn.jpg";

export default function ÜberUns() {
  return (
    <ProductPage
      productName="Über uns – Gorji Software Design International GmbH"
      hero={{
        title: "Gorji Software Design International GmbH",
        subtitle:
          "Seit 1996 entwickeln wir innovative Softwarelösungen für den Einzel- und Großhandel – mit über 25 Jahren Erfahrung, Branchenverständnis und echter Kundennähe.",
        buttonLeft: {
          title: "Kontakt aufnehmen",
          href: "/support",
        },
        buttonRight: { title: "Preise", href: "/preise" },
        image: ImageHero,
        imageAlt: "Gorji SDI Standort Eschborn",
      }}
      highlight={{
        image: ImageHighlight,
        bullets: [
          <>
            <strong>Über 25 Jahre Erfahrung</strong> in der Softwareentwicklung
            für den Handel.
          </>,
          <>
            <strong>Individuelle Lösungen</strong> – maßgeschneidert für
            Einzelhandel, Großhandel und Buchhaltung.
          </>,
          <>
            <strong>Ihr Partner</strong> für Programmierung, Beratung,
            Online-Shop-Lösungen und Marketing.
          </>,
        ],
      }}
      beforeVideo={
        <Section className="bg-white py-4 md:py-6 mt-20 mb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            {/* TEXT BLOCK */}
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                Wer wir sind
              </h2>

              <p className="text-slate-700 leading-relaxed">
                Die <strong>Gorji Software Design International GmbH</strong>{" "}
                wurde 1996 gegründet und entwickelt seitdem innovative
                Softwarelösungen für den Einzel- und Großhandel.
              </p>

              <p className="mt-4 text-slate-700 leading-relaxed">
                Mit über 25 Jahren Erfahrung verbinden wir technisches Know-how,
                Branchenverständnis und eine enge Zusammenarbeit mit unseren
                Kunden.
              </p>

              <ul className="mt-4 space-y-2">
                <li className="flex items-start gap-3 text-slate-700">
                  <Check
                    className="shrink-0 mt-1 text-brand-primary"
                    size={18}
                  />
                  <span>
                    <strong>Über 25 Jahre Erfahrung</strong>
                  </span>
                </li>

                <li className="flex items-start gap-3 text-slate-700">
                  <Check
                    className="shrink-0 mt-1 text-brand-primary"
                    size={18}
                  />
                  <span>
                    <strong>Branchenfokus Handel</strong>
                  </span>
                </li>

                <li className="flex items-start gap-3 text-slate-700">
                  <Check
                    className="shrink-0 mt-1 text-brand-primary"
                    size={18}
                  />
                  <span>
                    <strong>Individuelle Lösungen</strong>
                  </span>
                </li>
              </ul>
            </div>

            {/* VIDEO BLOCK */}
            <div className="relative overflow-hidden rounded-2xl border border-black/10 bg-black shadow-sm aspect-video mt-12">
              <video className="h-full w-full object-cover" controls>
                <source src="/VideoFürWebseite.mp4" type="video/mp4" />
              </video>
            </div>
          </div>

          {/* STÄRKEN + ANSPRUCH */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* STÄRKEN */}
            <div>
              <h3 className="font-bold text-2xl text-slate-900 mb-4">
                Unsere Stärken
              </h3>

              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-slate-700">
                  <Check
                    className="mt-1 text-brand-primary shrink-0"
                    size={18}
                  />
                  Maßgeschneiderte Software für Einzelhandel, Großhandel und
                  Buchhaltung
                </li>

                <li className="flex items-start gap-3 text-slate-700">
                  <Check
                    className="mt-1 text-brand-primary shrink-0"
                    size={18}
                  />
                  Flexible Anpassungen an individuelle Kundenwünsche
                </li>

                <li className="flex items-start gap-3 text-slate-700">
                  <Check
                    className="mt-1 text-brand-primary shrink-0"
                    size={18}
                  />
                  Laufende Weiterentwicklung durch Feedback aus der Praxis
                </li>

                <li className="flex items-start gap-3 text-slate-700">
                  <Check
                    className="mt-1 text-brand-primary shrink-0"
                    size={18}
                  />
                  Umfassender Support und Schulungen
                </li>

                <li className="flex items-start gap-3 text-slate-700">
                  <Check
                    className="mt-1 text-brand-primary shrink-0"
                    size={18}
                  />
                  Persönlicher Support –{" "}
                  <a href="/support" className="text-brand-primary underline">
                    zu den Support-Zeiten »
                  </a>
                </li>
              </ul>
            </div>

            {/* ANSPRUCH */}
            <div>
              <h3 className="font-bold text-2xl text-slate-900 mb-4">
                Unser Anspruch
              </h3>

              <p className="text-slate-700 leading-relaxed">
                Wir hören zu, entwickeln Lösungen und bleiben dran – bis wir die
                passende Antwort auf Ihr Anliegen gefunden haben. Dabei arbeiten
                wir zuverlässig, kundenorientiert und flexibel – seit 1996.
              </p>
            </div>
          </div>
        </Section>
      }
      faqs={[
        {
          q: "Seit wann gibt es die Software Design International GmbH?",
          a: "Wir wurden 1996 von Ramin Gorji (Dipl. Ing. Informatik) gegründet und blicken auf über 28 Jahre erfolgreiche Arbeit im Softwarebereich zurück.",
        },
        {
          q: "Für welche Branchen entwickeln Sie Software?",
          a: "Unser Schwerpunkt liegt auf Einzelhandel, Großhandel und Buchhaltung. Darüber hinaus beraten wir Sie gerne und empfehlen Ihnen genau das richtige Produkt für Ihre Bedürfnisse.",
        },
        {
          q: "Bieten Sie individuelle Anpassungen an?",
          a: "Ja, unsere Software wird nach Kundenwünschen stetig erweitert. Der Vorteil: Neu implementierte Funktionen sind anschließend auch für alle anderen Kunden verfügbar.",
        },
        {
          q: "Wie läuft der Support ab?",
          a: "Unsere Supportmitarbeiter sind von Montag bis Freitag von 09:30–13:00 Uhr und 14:00–18:00 Uhr per Telefon und E-Mail erreichbar.",
        },
        {
          q: "Wer steht hinter der Software Design International GmbH?",
          a: "Unser Team besteht aus qualifizierten Fachkräften mit langjähriger Erfahrung in Programmierung, Marketing, Online-Shop-Gestaltung, Consulting und Vertrieb.",
        },
        {
          q: "Wird die Software regelmäßig weiterentwickelt?",
          a: "Ja, wir erweitern unsere Produkte konsequent mit neuen Ideen und Kundenwünschen. Begegnen wir einem Problem, arbeiten wir so lange an einer Lösung, bis wir sie gefunden haben.",
        },
      ]}
      features={[]}
    />
  );
}
